import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheckCircle, FiClock, FiGlobe, FiMapPin, FiMinus, FiNavigation, FiPlus, FiSend, FiShoppingCart, FiTrash2, FiX } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import {
  buildDeviceAuthHeaders,
  completeDeviceCredential,
  createDeviceCredential,
  createOrderIdempotencyKey,
} from "./deviceCredential";
import "./guestOrder.css";

const BLANK_FAVICON = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
const QR_SESSION_STORAGE_KEY = "pilcore_pos_qr_ordering_session";
const DEFAULT_PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "credit_card", label: "Card" },
  { value: "room_charge", label: "Room Charge" },
];
const LANGUAGE_OPTIONS = {
  en: { label: "English", locale: "en-GB" },
  es: { label: "Español", locale: "es-ES" },
  fr: { label: "Français", locale: "fr-FR" },
  de: { label: "Deutsch", locale: "de-DE" },
  it: { label: "Italiano", locale: "it-IT" },
  pt: { label: "Português", locale: "pt-PT" },
  nl: { label: "Nederlands", locale: "nl-NL" },
  ca: { label: "Català", locale: "ca-ES" },
};
const UI_COPY = {
  en: {
    all: "All", other: "Other", openCart: "Open cart", pairing: "Pairing tablet...", loading: "Loading menu...",
    selectProduct: "Select at least one product.", selectPayment: "Select a payment method.", roomRequired: "Enter your room number for Room Charge.",
    nameRequired: "Enter your name.", invalidEmail: "Enter a valid email address.", sendError: "Could not send the order.",
    orderSent: "Order sent", copySent: "Copy sent to", emailFailed: "Order received, but the email copy could not be sent.", sendingCopy: "Sending a copy to",
    pricePending: "Price pending", item: "item", items: "items", yourOrder: "Your order", close: "Close", removeOne: "Remove one", addOne: "Add one",
    remove: "Remove", emptyCart: "Your cart is empty.", name: "Name", payment: "Payment method", room: "Room number",
    email: "Email for order copy (optional)", notes: "Notes for the team", total: "Total", sending: "Sending", send: "Send order",
    cash: "Cash", credit_card: "Card", room_charge: "Room Charge", other_payment: "Other",
  },
  es: {
    all: "Todo", other: "Otros", openCart: "Abrir carrito", pairing: "Vinculando tablet...", loading: "Cargando menú...",
    selectProduct: "Selecciona al menos un producto.", selectPayment: "Selecciona un método de pago.", roomRequired: "Introduce el número de habitación.",
    nameRequired: "Introduce tu nombre.", invalidEmail: "Introduce un email válido.", sendError: "No se ha podido enviar el pedido.",
    orderSent: "Pedido enviado", copySent: "Copia enviada a", emailFailed: "Pedido recibido, pero no se pudo enviar la copia por email.", sendingCopy: "Enviando una copia a",
    pricePending: "Precio pendiente", item: "artículo", items: "artículos", yourOrder: "Tu pedido", close: "Cerrar", removeOne: "Quitar uno", addOne: "Añadir uno",
    remove: "Quitar", emptyCart: "El carrito está vacío.", name: "Nombre", payment: "Método de pago", room: "Número de habitación",
    email: "Email para recibir una copia (opcional)", notes: "Notas para el equipo", total: "Total", sending: "Enviando", send: "Enviar pedido",
    cash: "Efectivo", credit_card: "Tarjeta", room_charge: "Cargo a habitación", other_payment: "Otro",
  },
  fr: {
    all: "Tout", other: "Autres", openCart: "Ouvrir le panier", pairing: "Connexion de la tablette...", loading: "Chargement du menu...",
    selectProduct: "Sélectionnez au moins un produit.", selectPayment: "Sélectionnez un moyen de paiement.", roomRequired: "Saisissez votre numéro de chambre.",
    nameRequired: "Saisissez votre nom.", invalidEmail: "Saisissez une adresse email valide.", sendError: "Impossible d'envoyer la commande.",
    orderSent: "Commande envoyée", copySent: "Copie envoyée à", emailFailed: "Commande reçue, mais la copie par email n'a pas pu être envoyée.", sendingCopy: "Envoi d'une copie à",
    pricePending: "Prix en attente", item: "article", items: "articles", yourOrder: "Votre commande", close: "Fermer", removeOne: "Retirer un", addOne: "Ajouter un",
    remove: "Retirer", emptyCart: "Votre panier est vide.", name: "Nom", payment: "Moyen de paiement", room: "Numéro de chambre",
    email: "Email pour recevoir une copie (facultatif)", notes: "Notes pour l'équipe", total: "Total", sending: "Envoi", send: "Envoyer la commande",
    cash: "Espèces", credit_card: "Carte", room_charge: "Note de chambre", other_payment: "Autre",
  },
  de: {
    all: "Alle", other: "Andere", openCart: "Warenkorb öffnen", pairing: "Tablet wird verbunden...", loading: "Menü wird geladen...",
    selectProduct: "Wählen Sie mindestens ein Produkt.", selectPayment: "Wählen Sie eine Zahlungsmethode.", roomRequired: "Geben Sie Ihre Zimmernummer ein.",
    nameRequired: "Geben Sie Ihren Namen ein.", invalidEmail: "Geben Sie eine gültige E-Mail-Adresse ein.", sendError: "Bestellung konnte nicht gesendet werden.",
    orderSent: "Bestellung gesendet", copySent: "Kopie gesendet an", emailFailed: "Bestellung eingegangen, aber die E-Mail-Kopie konnte nicht gesendet werden.", sendingCopy: "Kopie wird gesendet an",
    pricePending: "Preis ausstehend", item: "Artikel", items: "Artikel", yourOrder: "Ihre Bestellung", close: "Schließen", removeOne: "Einen entfernen", addOne: "Einen hinzufügen",
    remove: "Entfernen", emptyCart: "Ihr Warenkorb ist leer.", name: "Name", payment: "Zahlungsmethode", room: "Zimmernummer",
    email: "E-Mail für Bestellkopie (optional)", notes: "Hinweise für das Team", total: "Gesamt", sending: "Wird gesendet", send: "Bestellung senden",
    cash: "Bar", credit_card: "Karte", room_charge: "Zimmerrechnung", other_payment: "Andere",
  },
  it: {
    all: "Tutto", other: "Altro", openCart: "Apri carrello", pairing: "Collegamento tablet...", loading: "Caricamento menu...",
    selectProduct: "Seleziona almeno un prodotto.", selectPayment: "Seleziona un metodo di pagamento.", roomRequired: "Inserisci il numero della camera.",
    nameRequired: "Inserisci il tuo nome.", invalidEmail: "Inserisci un indirizzo email valido.", sendError: "Impossibile inviare l'ordine.",
    orderSent: "Ordine inviato", copySent: "Copia inviata a", emailFailed: "Ordine ricevuto, ma non è stato possibile inviare la copia via email.", sendingCopy: "Invio di una copia a",
    pricePending: "Prezzo in sospeso", item: "articolo", items: "articoli", yourOrder: "Il tuo ordine", close: "Chiudi", removeOne: "Rimuovi uno", addOne: "Aggiungi uno",
    remove: "Rimuovi", emptyCart: "Il carrello è vuoto.", name: "Nome", payment: "Metodo di pagamento", room: "Numero camera",
    email: "Email per ricevere una copia (facoltativo)", notes: "Note per lo staff", total: "Totale", sending: "Invio", send: "Invia ordine",
    cash: "Contanti", credit_card: "Carta", room_charge: "Addebito in camera", other_payment: "Altro",
  },
  pt: {
    all: "Tudo", other: "Outros", openCart: "Abrir carrinho", pairing: "A ligar o tablet...", loading: "A carregar o menu...",
    selectProduct: "Selecione pelo menos um produto.", selectPayment: "Selecione um método de pagamento.", roomRequired: "Introduza o número do quarto.",
    nameRequired: "Introduza o seu nome.", invalidEmail: "Introduza um email válido.", sendError: "Não foi possível enviar o pedido.",
    orderSent: "Pedido enviado", copySent: "Cópia enviada para", emailFailed: "Pedido recebido, mas não foi possível enviar a cópia por email.", sendingCopy: "A enviar uma cópia para",
    pricePending: "Preço pendente", item: "artigo", items: "artigos", yourOrder: "O seu pedido", close: "Fechar", removeOne: "Remover um", addOne: "Adicionar um",
    remove: "Remover", emptyCart: "O carrinho está vazio.", name: "Nome", payment: "Método de pagamento", room: "Número do quarto",
    email: "Email para receber uma cópia (opcional)", notes: "Notas para a equipa", total: "Total", sending: "A enviar", send: "Enviar pedido",
    cash: "Dinheiro", credit_card: "Cartão", room_charge: "Débito no quarto", other_payment: "Outro",
  },
  nl: {
    all: "Alles", other: "Overig", openCart: "Winkelwagen openen", pairing: "Tablet koppelen...", loading: "Menu laden...",
    selectProduct: "Selecteer minstens één product.", selectPayment: "Selecteer een betaalmethode.", roomRequired: "Voer uw kamernummer in.",
    nameRequired: "Voer uw naam in.", invalidEmail: "Voer een geldig e-mailadres in.", sendError: "De bestelling kon niet worden verzonden.",
    orderSent: "Bestelling verzonden", copySent: "Kopie verzonden naar", emailFailed: "Bestelling ontvangen, maar de e-mailkopie kon niet worden verzonden.", sendingCopy: "Kopie wordt verzonden naar",
    pricePending: "Prijs volgt", item: "artikel", items: "artikelen", yourOrder: "Uw bestelling", close: "Sluiten", removeOne: "Eén verwijderen", addOne: "Eén toevoegen",
    remove: "Verwijderen", emptyCart: "Uw winkelwagen is leeg.", name: "Naam", payment: "Betaalmethode", room: "Kamernummer",
    email: "E-mail voor een kopie (optioneel)", notes: "Opmerkingen voor het team", total: "Totaal", sending: "Verzenden", send: "Bestelling verzenden",
    cash: "Contant", credit_card: "Kaart", room_charge: "Op kamer", other_payment: "Overig",
  },
  ca: {
    all: "Tot", other: "Altres", openCart: "Obrir cistella", pairing: "Vinculant tauleta...", loading: "Carregant menú...",
    selectProduct: "Selecciona almenys un producte.", selectPayment: "Selecciona un mètode de pagament.", roomRequired: "Introdueix el número d'habitació.",
    nameRequired: "Introdueix el teu nom.", invalidEmail: "Introdueix un email vàlid.", sendError: "No s'ha pogut enviar la comanda.",
    orderSent: "Comanda enviada", copySent: "Còpia enviada a", emailFailed: "Comanda rebuda, però no s'ha pogut enviar la còpia per email.", sendingCopy: "Enviant una còpia a",
    pricePending: "Preu pendent", item: "article", items: "articles", yourOrder: "La teva comanda", close: "Tancar", removeOne: "Treure'n un", addOne: "Afegir-ne un",
    remove: "Treure", emptyCart: "La cistella és buida.", name: "Nom", payment: "Mètode de pagament", room: "Número d'habitació",
    email: "Email per rebre una còpia (opcional)", notes: "Notes per a l'equip", total: "Total", sending: "Enviant", send: "Enviar comanda",
    cash: "Efectiu", credit_card: "Targeta", room_charge: "Càrrec a l'habitació", other_payment: "Altres",
  },
};

const QR_COPY = {
  en: {
    startOrder: "Start order", verifyingLocation: "Verifying location", locationTitle: "Order from your table",
    locationBody: "We will verify that you are at this outlet. Your ordering window will last {minutes} minutes.",
    locationPermission: "Allow location when your browser asks.", locationUnavailable: "Your current location could not be verified.",
    sessionExpiredTitle: "Ordering window ended", sessionExpiredBody: "Scan the QR code on your table again to start a new order.",
    sessionTime: "Ordering time", tableLabel: "Table",
  },
  es: {
    startOrder: "Empezar pedido", verifyingLocation: "Verificando ubicación", locationTitle: "Pide desde tu mesa",
    locationBody: "Comprobaremos que estás en este outlet. Tendrás {minutes} minutos para realizar el pedido.",
    locationPermission: "Permite el acceso a la ubicación cuando lo solicite el navegador.", locationUnavailable: "No se ha podido verificar tu ubicación actual.",
    sessionExpiredTitle: "Tiempo de pedido finalizado", sessionExpiredBody: "Vuelve a escanear el código QR de la mesa para comenzar otro pedido.",
    sessionTime: "Tiempo para pedir", tableLabel: "Mesa",
  },
  fr: {
    startOrder: "Commencer", verifyingLocation: "Vérification de la position", locationTitle: "Commandez depuis votre table",
    locationBody: "Nous vérifierons votre présence. Vous aurez {minutes} minutes pour commander.", locationPermission: "Autorisez la localisation lorsque le navigateur le demande.",
    locationUnavailable: "Votre position n'a pas pu être vérifiée.", sessionExpiredTitle: "Temps de commande terminé", sessionExpiredBody: "Scannez à nouveau le QR code de la table.", sessionTime: "Temps restant", tableLabel: "Table",
  },
  de: {
    startOrder: "Bestellung starten", verifyingLocation: "Standort wird geprüft", locationTitle: "Am Tisch bestellen",
    locationBody: "Wir prüfen Ihren Standort. Sie haben {minutes} Minuten zum Bestellen.", locationPermission: "Erlauben Sie den Standortzugriff im Browser.",
    locationUnavailable: "Ihr Standort konnte nicht bestätigt werden.", sessionExpiredTitle: "Bestellzeit beendet", sessionExpiredBody: "Scannen Sie den QR-Code am Tisch erneut.", sessionTime: "Verbleibende Zeit", tableLabel: "Tisch",
  },
  it: {
    startOrder: "Inizia ordine", verifyingLocation: "Verifica posizione", locationTitle: "Ordina dal tavolo",
    locationBody: "Verificheremo la tua posizione. Avrai {minutes} minuti per ordinare.", locationPermission: "Consenti la posizione quando richiesto dal browser.",
    locationUnavailable: "Non è stato possibile verificare la posizione.", sessionExpiredTitle: "Tempo scaduto", sessionExpiredBody: "Scansiona di nuovo il QR del tavolo.", sessionTime: "Tempo rimasto", tableLabel: "Tavolo",
  },
  pt: {
    startOrder: "Iniciar pedido", verifyingLocation: "A verificar localização", locationTitle: "Peça na sua mesa",
    locationBody: "Vamos verificar a sua localização. Terá {minutes} minutos para pedir.", locationPermission: "Permita a localização quando o navegador pedir.",
    locationUnavailable: "Não foi possível verificar a localização.", sessionExpiredTitle: "Tempo terminado", sessionExpiredBody: "Leia novamente o QR da mesa.", sessionTime: "Tempo restante", tableLabel: "Mesa",
  },
  nl: {
    startOrder: "Bestelling starten", verifyingLocation: "Locatie controleren", locationTitle: "Bestel vanaf uw tafel",
    locationBody: "We controleren uw locatie. U krijgt {minutes} minuten om te bestellen.", locationPermission: "Sta locatie toe wanneer de browser erom vraagt.",
    locationUnavailable: "Uw locatie kon niet worden bevestigd.", sessionExpiredTitle: "Besteltijd afgelopen", sessionExpiredBody: "Scan de QR-code op tafel opnieuw.", sessionTime: "Resterende tijd", tableLabel: "Tafel",
  },
  ca: {
    startOrder: "Començar comanda", verifyingLocation: "Verificant ubicació", locationTitle: "Demana des de la taula",
    locationBody: "Comprovarem la ubicació. Tindràs {minutes} minuts per demanar.", locationPermission: "Permet la ubicació quan ho demani el navegador.",
    locationUnavailable: "No s'ha pogut verificar la ubicació.", sessionExpiredTitle: "Temps finalitzat", sessionExpiredBody: "Torna a escanejar el QR de la taula.", sessionTime: "Temps restant", tableLabel: "Taula",
  },
};

function translatedText(translations, language, fallbackLanguage, fallback = "") {
  return translations?.[language]
    || translations?.[fallbackLanguage]
    || Object.values(translations || {}).find(Boolean)
    || fallback;
}

function isValidEmail(value) {
  const email = String(value || "").trim();
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function GuestOrderPage({ orderMode = "tablet" }) {
  const { token, tableToken } = useParams();
  const navigate = useNavigate();
  const isQrEntry = orderMode === "tableQr";
  const isQrSessionView = orderMode === "tableQrSession";
  const isQrMode = isQrEntry || isQrSessionView;
  const pairingStartedRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  const browserBrandingRef = useRef(null);
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [language, setLanguage] = useState("en");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState(() => new Map());
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [orderNote, setOrderNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successOrder, setSuccessOrder] = useState(null);
  const [pairing, setPairing] = useState(Boolean(token));
  const [showCart, setShowCart] = useState(false);
  const [qrTable, setQrTable] = useState(null);
  const [qrSessionToken, setQrSessionToken] = useState("");
  const [qrSessionExpiresAt, setQrSessionExpiresAt] = useState(null);
  const [qrSessionState, setQrSessionState] = useState(isQrSessionView ? "expired" : "ready");
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const enabledLanguages = useMemo(() => {
    const configured = catalog?.guest_enabled_languages;
    return configured?.length ? configured : [catalog?.guest_default_language || "en"];
  }, [catalog]);
  const defaultLanguage = catalog?.guest_default_language || enabledLanguages[0] || "en";
  const copy = { ...UI_COPY.en, ...(UI_COPY[language] || {}), ...QR_COPY.en, ...(QR_COPY[language] || {}) };
  const formatMoney = useCallback((value) => (
    new Intl.NumberFormat(LANGUAGE_OPTIONS[language]?.locale || "en-GB", { style: "currency", currency: "EUR" })
      .format(Number(value || 0))
  ), [language]);
  const productName = useCallback((product) => translatedText(
    product?.name_translations,
    language,
    defaultLanguage,
    product?.pos_product?.name || ""
  ), [defaultLanguage, language]);
  const categoryName = useCallback((category) => translatedText(
    category?.name_translations,
    language,
    defaultLanguage,
    copy.other
  ), [copy.other, defaultLanguage, language]);
  const productSubtitle = useCallback((product) => translatedText(
    product?.description_translations,
    language,
    defaultLanguage,
    product?.category ? categoryName(product.category) : ""
  ), [categoryName, defaultLanguage, language]);

  useEffect(() => {
    const existingFavicon = document.querySelector('link[rel~="icon"]');
    const favicon = existingFavicon || document.createElement("link");
    if (!existingFavicon) {
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }
    browserBrandingRef.current = {
      favicon,
      previousHref: existingFavicon?.getAttribute("href") || "",
      previousTitle: document.title,
      createdFavicon: !existingFavicon,
    };
    document.title = "Order";
    favicon.href = BLANK_FAVICON;

    return () => {
      const branding = browserBrandingRef.current;
      if (!branding) return;
      document.title = branding.previousTitle;
      if (branding.createdFavicon) branding.favicon.remove();
      else branding.favicon.setAttribute("href", branding.previousHref);
      browserBrandingRef.current = null;
    };
  }, []);

  useEffect(() => {
    const favicon = browserBrandingRef.current?.favicon;
    if (favicon) favicon.href = catalog?.guest_logo_url || BLANK_FAVICON;
  }, [catalog?.guest_logo_url]);

  useEffect(() => {
    if (!successOrder) return undefined;
    const timer = window.setTimeout(() => {
      setSuccessOrder(null);
      setError("");
      setSelectedCategory("all");
      if (isQrMode) setProducts([]);
      window.scrollTo({ top: 0, behavior: "auto" });
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [isQrMode, successOrder]);

  const applyCatalogResponse = useCallback((data) => {
    const nextCatalog = data?.catalog || data?.device?.catalog || data?.table?.catalog || null;
    const languages = nextCatalog?.guest_enabled_languages?.length
      ? nextCatalog.guest_enabled_languages
      : [nextCatalog?.guest_default_language || "en"];
    const browserLanguage = String(window.navigator.language || "").split("-")[0].toLowerCase();
    setCatalog(nextCatalog);
    setProducts(data?.products || []);
    if (data?.table) setQrTable(data.table);
    setLanguage((current) => languages.includes(current)
      ? current
      : languages.includes(browserLanguage)
        ? browserLanguage
        : nextCatalog?.guest_default_language || languages[0]);
    setPaymentMethods(data?.payment_methods?.length ? data.payment_methods : DEFAULT_PAYMENT_METHODS);
  }, []);

  const loadTabletCatalog = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const headers = await buildDeviceAuthHeaders("GET");
      const res = await axios.get("/api/pos/guest-device/catalog", { headers, withCredentials: true });
      applyCatalogResponse(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "This ordering tablet is not available.");
    } finally {
      setLoading(false);
    }
  }, [applyCatalogResponse]);

  const loadQrSessionCatalog = useCallback(async (sessionToken) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/qr-session/catalog", {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      applyCatalogResponse(res.data);
      setQrSessionToken(sessionToken);
      setQrSessionExpiresAt(res.data?.expires_at || null);
      setQrSessionState("active");
    } catch (err) {
      window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
      setQrSessionState("expired");
      setError(err?.response?.data?.error || copy.sessionExpiredBody);
    } finally {
      setLoading(false);
    }
  }, [applyCatalogResponse, copy.sessionExpiredBody]);

  useEffect(() => {
    if (isQrEntry) {
      const loadQrEntry = async () => {
        try {
          setLoading(true);
          setError("");
          const res = await axios.get(`/api/pos/qr-tables/public/${encodeURIComponent(tableToken || "")}`);
          applyCatalogResponse(res.data);
          setQrSessionState("ready");
        } catch (err) {
          setError(err?.response?.data?.error || "This table ordering page is not available.");
          setQrSessionState("unavailable");
        } finally {
          setLoading(false);
        }
      };
      loadQrEntry();
      return;
    }

    if (isQrSessionView) {
      let stored = null;
      try {
        stored = JSON.parse(window.sessionStorage.getItem(QR_SESSION_STORAGE_KEY) || "null");
      } catch (_err) {
        stored = null;
      }
      if (stored?.session_token && new Date(stored.expires_at).getTime() > Date.now()) {
        setQrTable(stored.table || null);
        loadQrSessionCatalog(stored.session_token);
      } else {
        window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
        setQrSessionState("expired");
        setLoading(false);
      }
      return;
    }

    if (!token) {
      setPairing(false);
      loadTabletCatalog();
      return;
    }
    if (pairingStartedRef.current) return;
    pairingStartedRef.current = true;

    const pairDevice = async () => {
      try {
        setPairing(true);
        setLoading(true);
        setError("");
        const credential = await createDeviceCredential();
        const res = await axios.post(
          `/api/pos/device-pairing/${encodeURIComponent(token)}`,
          {
            public_key_jwk: credential.publicKeyJwk,
            expected_device_type: "guest_order_tablet",
          },
          { withCredentials: true }
        );
        await completeDeviceCredential(credential, res.data?.device);
        navigate(res.data?.redirect_path || "/pos/guest-order", { replace: true });
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "This pairing link is not available.");
        setLoading(false);
      } finally {
        setPairing(false);
      }
    };

    pairDevice();
  }, [applyCatalogResponse, isQrEntry, isQrSessionView, loadQrSessionCatalog, loadTabletCatalog, navigate, tableToken, token]);

  useEffect(() => {
    if (!isQrSessionView || qrSessionState !== "active" || !qrSessionExpiresAt) return undefined;
    const updateRemaining = () => {
      const seconds = Math.max(0, Math.ceil((new Date(qrSessionExpiresAt).getTime() - Date.now()) / 1000));
      setRemainingSeconds(seconds);
      if (!seconds) {
        window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
        setQrSessionState("expired");
        setQrSessionToken("");
        setProducts([]);
        setCart(new Map());
        setShowCart(false);
      }
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [isQrSessionView, qrSessionExpiresAt, qrSessionState]);

  const startQrSession = () => {
    if (!navigator.geolocation || !tableToken) {
      setError(copy.locationUnavailable);
      return;
    }
    setError("");
    setQrSessionState("locating");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await axios.post(`/api/pos/qr-tables/public/${encodeURIComponent(tableToken)}/session`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
          const stored = {
            session_token: res.data?.session_token,
            expires_at: res.data?.expires_at,
            table: res.data?.table || qrTable,
          };
          window.sessionStorage.setItem(QR_SESSION_STORAGE_KEY, JSON.stringify(stored));
          navigate("/pos/table-order/session", { replace: true });
        } catch (err) {
          setError(err?.response?.data?.error || copy.locationUnavailable);
          setQrSessionState("ready");
        }
      },
      (locationError) => {
        setError(locationError?.message || copy.locationUnavailable);
        setQrSessionState("ready");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const categoryOptions = useMemo(() => {
    const categories = new Map();
    products.forEach((product) => {
      const key = product.category_id ? String(product.category_id) : "other";
      if (!categories.has(key)) categories.set(key, product.category || null);
    });
    return Array.from(categories, ([value, category]) => ({ value, label: category ? categoryName(category) : copy.other }));
  }, [categoryName, copy.other, products]);

  const visibleProducts = useMemo(() => {
    if (selectedCategory === "all") return products;
    return products.filter((product) => (
      product.category_id ? String(product.category_id) : "other"
    ) === selectedCategory);
  }, [products, selectedCategory]);

  const cartLines = useMemo(() => Array.from(cart.values())
    .filter((line) => line.quantity > 0)
    .map((line) => ({ ...line, line_amount: Number(line.unit_price || 0) * Number(line.quantity || 0) })), [cart]);
  const cartTotal = useMemo(() => cartLines.reduce((sum, line) => sum + Number(line.line_amount || 0), 0), [cartLines]);
  const cartItemCount = useMemo(() => cartLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0), [cartLines]);
  const isRoomCharge = paymentMethod === "room_charge";
  const sessionTimerLabel = `${String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:${String(remainingSeconds % 60).padStart(2, "0")}`;

  const updateQuantity = (product, delta) => {
    setSuccessOrder(null);
    setError("");
    idempotencyKeyRef.current = null;
    setCart((current) => {
      const next = new Map(current);
      const existing = next.get(product.id) || { ...product, quantity: 0 };
      const quantity = Math.max(0, Math.min(20, Number(existing.quantity || 0) + delta));
      if (!quantity) next.delete(product.id);
      else next.set(product.id, { ...existing, quantity });
      return next;
    });
  };

  const submitOrder = async () => {
    if (!cartLines.length) return setError(copy.selectProduct);
    if (!paymentMethod) return setError(copy.selectPayment);
    if (isRoomCharge && !roomNumber.trim()) return setError(copy.roomRequired);
    if (!customerName.trim() && !roomNumber.trim()) return setError(copy.nameRequired);
    const normalizedEmail = customerEmail.trim();
    if (normalizedEmail && !isValidEmail(normalizedEmail)) return setError(copy.invalidEmail);

    try {
      setSubmitting(true);
      setError("");
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createOrderIdempotencyKey();
      const payload = {
        idempotency_key: idempotencyKeyRef.current,
        customer_name: customerName.trim(),
        customer_email: normalizedEmail,
        payment_method_category: paymentMethod,
        room_number: isRoomCharge ? roomNumber.trim() : "",
        order_note: orderNote.trim(),
        items: cartLines.map((line) => ({ product_id: line.pos_product_id, quantity: line.quantity })),
      };
      const res = isQrMode
        ? await axios.post("/api/pos/qr-session/orders", payload, { headers: { Authorization: `Bearer ${qrSessionToken}` } })
        : await axios.post(
            "/api/pos/guest-device/orders",
            payload,
            { headers: await buildDeviceAuthHeaders("POST", payload), withCredentials: true }
          );
      setSuccessOrder(res.data?.order || null);
      if (isQrMode) {
        window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
        setQrSessionState("completed");
        setQrSessionToken("");
      }
      idempotencyKeyRef.current = null;
      setCart(new Map());
      setCustomerName("");
      setCustomerEmail("");
      setPaymentMethod("");
      setRoomNumber("");
      setOrderNote("");
      setSelectedCategory("all");
      setShowCart(false);
    } catch (err) {
      setError(err?.response?.data?.error || copy.sendError);
      if (isQrMode && ["QR_SESSION_EXPIRED", "QR_SESSION_CLOSED"].includes(err?.response?.data?.code)) {
        window.sessionStorage.removeItem(QR_SESSION_STORAGE_KEY);
        setQrSessionState("expired");
        setProducts([]);
        setShowCart(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="guestOrderPage"><div className="guestOrderState">{pairing ? copy.pairing : copy.loading}</div></div>;
  }
  if (!isQrMode && error && !products.length) {
    return <div className="guestOrderPage"><div className="guestOrderState guestOrderState--error">{error}</div></div>;
  }

  const qrGateVisible = isQrMode && qrSessionState !== "active" && !successOrder;
  if (qrGateVisible) {
    const sessionMinutes = Number(qrTable?.session_minutes || 10);
    const canStart = isQrEntry && ["ready", "locating"].includes(qrSessionState);
    const unavailable = qrSessionState === "unavailable";
    return (
      <div
        className={`guestOrderPage guestOrderAccessPage ${catalog?.guest_background_url ? "guestOrderPage--with-background" : ""}`}
        data-theme={catalog?.guest_theme || "dark"}
        lang={language}
        style={catalog?.guest_background_url ? { backgroundImage: `url(${JSON.stringify(catalog.guest_background_url)})` } : undefined}
      >
        <header className="guestOrderHeader guestOrderAccessHeader">
          {catalog?.guest_logo_url ? <img className="guestOrderLogo" src={catalog.guest_logo_url} alt="" /> : <span />}
          {enabledLanguages.length > 1 ? (
            <label className="guestOrderLanguage">
              <FiGlobe aria-hidden="true" />
              <select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label="Language">
                {enabledLanguages.map((value) => <option key={value} value={value}>{LANGUAGE_OPTIONS[value]?.label || value.toUpperCase()}</option>)}
              </select>
            </label>
          ) : null}
        </header>
        <main className="guestOrderAccessGate">
          <FiMapPin className="guestOrderAccessIcon" aria-hidden="true" />
          <div className="guestOrderAccessCopy">
            <span>{qrTable?.outlet_name || ""}</span>
            <h1>{unavailable ? copy.sessionExpiredTitle : canStart ? copy.locationTitle : copy.sessionExpiredTitle}</h1>
            {qrTable?.name ? <strong>{copy.tableLabel}: {qrTable.name}</strong> : null}
            <p>{canStart
              ? copy.locationBody.replace("{minutes}", sessionMinutes)
              : copy.sessionExpiredBody}</p>
            {canStart ? <small>{copy.locationPermission}</small> : null}
          </div>
          {error ? <div className="guestOrderError guestOrderAccessError">{error}</div> : null}
          {canStart ? (
            <button type="button" className="guestOrderStartButton" onClick={startQrSession} disabled={qrSessionState === "locating"}>
              <FiNavigation /> {qrSessionState === "locating" ? copy.verifyingLocation : copy.startOrder}
            </button>
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <div
      className={`guestOrderPage ${catalog?.guest_background_url ? "guestOrderPage--with-background" : ""}`}
      data-theme={catalog?.guest_theme || "dark"}
      lang={language}
      style={catalog?.guest_background_url ? { backgroundImage: `url(${JSON.stringify(catalog.guest_background_url)})` } : undefined}
    >
      <header className="guestOrderHeader">
        {catalog?.guest_logo_url ? <img className="guestOrderLogo" src={catalog.guest_logo_url} alt="" /> : <span />}
        <div className="guestOrderHeaderActions">
          {isQrMode ? (
            <span className={`guestOrderSessionTimer ${remainingSeconds <= 120 ? "guestOrderSessionTimer--warning" : ""}`} title={copy.sessionTime}>
              <FiClock aria-hidden="true" /> {sessionTimerLabel}
            </span>
          ) : null}
          {enabledLanguages.length > 1 ? (
            <label className="guestOrderLanguage">
              <FiGlobe aria-hidden="true" />
              <select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label="Language">
                {enabledLanguages.map((value) => <option key={value} value={value}>{LANGUAGE_OPTIONS[value]?.label || value.toUpperCase()}</option>)}
              </select>
            </label>
          ) : null}
          <button type="button" className="guestOrderCartPill" onClick={() => setShowCart(true)} aria-label={copy.openCart}>
            <FiShoppingCart /><span>{cartItemCount}</span>
          </button>
        </div>
      </header>

      {successOrder ? (
        <div className="guestOrderSuccess">
          <FiCheckCircle />
          <div>
            <strong>{copy.orderSent}</strong><span>{successOrder.order_number}</span>
            {successOrder.customer_email ? (
              <small className={successOrder.confirmation_email_status === "failed" ? "guestOrderSuccessEmail--failed" : ""}>
                {successOrder.confirmation_email_status === "sent"
                  ? `${copy.copySent} ${successOrder.customer_email}`
                  : successOrder.confirmation_email_status === "failed"
                    ? copy.emailFailed
                    : `${copy.sendingCopy} ${successOrder.customer_email}`}
              </small>
            ) : null}
          </div>
        </div>
      ) : null}
      {error ? <div className="guestOrderError">{error}</div> : null}

      <nav className="guestOrderFilters" aria-label="Menu categories">
        <button type="button" className={selectedCategory === "all" ? "guestOrderFilter--active" : ""} onClick={() => setSelectedCategory("all")}>{copy.all}</button>
        {categoryOptions.map((option) => (
          <button key={option.value} type="button" className={selectedCategory === option.value ? "guestOrderFilter--active" : ""} onClick={() => setSelectedCategory(option.value)}>{option.label}</button>
        ))}
      </nav>

      <main className="guestOrderGrid">
        {visibleProducts.map((product) => {
          const quantity = cart.get(product.id)?.quantity || 0;
          const name = productName(product);
          const subtitle = productSubtitle(product);
          return (
            <article key={product.id} className={`guestOrderProduct ${quantity ? "guestOrderProduct--selected" : ""}`}>
              <div className="guestOrderProductHeading">
                <strong>{name}</strong>
                {subtitle ? <span>{subtitle}</span> : null}
              </div>
              <div className="guestOrderProductControls" aria-label={`${name}: ${quantity}`}>
                <button type="button" onClick={() => updateQuantity(product, -1)} disabled={!quantity} aria-label={`${copy.removeOne} ${name}`} title={copy.removeOne}><FiMinus /></button>
                <strong>{quantity}</strong>
                <button type="button" onClick={() => updateQuantity(product, 1)} disabled={quantity >= 20} aria-label={`${copy.addOne} ${name}`} title={copy.addOne}><FiPlus /></button>
              </div>
              <div className="guestOrderProductFooter"><span>{Number(product.unit_price || 0) > 0 ? formatMoney(product.unit_price) : copy.pricePending}</span></div>
            </article>
          );
        })}
      </main>

      {cartItemCount ? (
        <button type="button" className="guestOrderCheckoutBar" onClick={() => setShowCart(true)}>
          <span><FiShoppingCart /> {cartItemCount} {cartItemCount === 1 ? copy.item : copy.items}</span><strong>{formatMoney(cartTotal)}</strong>
        </button>
      ) : null}

      {showCart ? (
        <div className="guestOrderCartOverlay" role="presentation">
          <section className="guestOrderCart" role="dialog" aria-modal="true" aria-label={copy.yourOrder}>
            <header className="guestOrderCartHeader">
              <div><h2>{copy.yourOrder}</h2><span>{cartItemCount} {cartItemCount === 1 ? copy.item : copy.items}</span></div>
              <button type="button" className="guestOrderIconButton" onClick={() => setShowCart(false)} aria-label={copy.close} title={copy.close}><FiX /></button>
            </header>
            <div className="guestOrderCartLines">
              {error ? <div className="guestOrderError guestOrderCartError">{error}</div> : null}
              {cartLines.map((line) => {
                const name = productName(line);
                return (
                  <div key={line.id} className="guestOrderCartLine">
                    <div><strong>{name}</strong><span>{formatMoney(line.line_amount)}</span></div>
                    <div className="guestOrderCartQuantity">
                      <button type="button" onClick={() => updateQuantity(line, -1)} aria-label={`${copy.removeOne} ${name}`} title={copy.removeOne}><FiMinus /></button>
                      <strong>{line.quantity}</strong>
                      <button type="button" onClick={() => updateQuantity(line, 1)} aria-label={`${copy.addOne} ${name}`} title={copy.addOne}><FiPlus /></button>
                      <button type="button" className="guestOrderCartRemove" onClick={() => updateQuantity(line, -line.quantity)} aria-label={`${copy.remove} ${name}`} title={copy.remove}><FiTrash2 /></button>
                    </div>
                  </div>
                );
              })}
              {!cartLines.length ? <div className="guestOrderCartEmpty">{copy.emptyCart}</div> : null}
            </div>
            <div className="guestOrderCartForm">
              <div className="guestOrderIdentity">
                <input value={customerName} onChange={(event) => { idempotencyKeyRef.current = null; setCustomerName(event.target.value); }} placeholder={copy.name} />
                <select value={paymentMethod} onChange={(event) => { idempotencyKeyRef.current = null; setPaymentMethod(event.target.value); if (event.target.value !== "room_charge") setRoomNumber(""); setError(""); }} aria-label={copy.payment}>
                  <option value="">{copy.payment}</option>
                  {paymentMethods.map((method) => <option key={method.value} value={method.value}>{copy[method.value] || method.label}</option>)}
                </select>
                {isRoomCharge ? <input className="guestOrderRoomInput" value={roomNumber} onChange={(event) => { idempotencyKeyRef.current = null; setRoomNumber(event.target.value); }} placeholder={copy.room} inputMode="numeric" /> : null}
                <input className="guestOrderEmailInput" type="email" value={customerEmail} onChange={(event) => { idempotencyKeyRef.current = null; setCustomerEmail(event.target.value); setError(""); }} placeholder={copy.email} inputMode="email" autoComplete="off" spellCheck="false" />
              </div>
              <textarea value={orderNote} onChange={(event) => { idempotencyKeyRef.current = null; setOrderNote(event.target.value); }} placeholder={copy.notes} rows={2} />
            </div>
            <footer className="guestOrderCartFooter">
              <div><span>{copy.total}</span><strong>{formatMoney(cartTotal)}</strong></div>
              <button type="button" onClick={submitOrder} disabled={submitting || !cartLines.length}><FiSend /> {submitting ? copy.sending : copy.send}</button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
